import { FC, memo, useCallback } from 'react'

import { ArrowRightIcon, CheckIcon, Link, Text } from '@cash-me/react-components'
import { useRouter } from 'next/router'

import { AccordionNoRadix } from 'components/accordionNoRadix'
import { AnchorButton } from 'components/anchor-button'
import { Infos, Section } from 'components/general-styles/General'
import { Grid, Col } from 'components/grid'
import { UnorderedList } from 'components/list'
import { buildSimulatorUrl } from 'utils/buildSimulatorUrl'
import { pushDataLayer } from 'utils/push-data-layer'

import { HelpProps, AccordionProps, ItemProps, ChecklistItemProps } from './help.model'
import * as S from './styles'

export const Help: FC<HelpProps> = memo(({ children, accordion, $bg }) => {
  const router = useRouter()

  const handleAccordionClick = (title: string) => {
    pushDataLayer({
      category: 'Clique',
      action: 'Ajuda',
      label: `Accordion Pergunta: ${title}`
    })
  }

  const handleLinkItemClick = useCallback(
    content => {
      const dataLayer = content.dataLayer
      const href = content.href

      pushDataLayer(dataLayer)

      if (href === 'simulador-home-equity')
        location.href = buildSimulatorUrl(router.pathname, { medium: 'Ajuda - ' + content.label })
      else router.push(href)
    },
    [router]
  )

  const renderItem = {
    text: (id, content) => (
      <S.Text key={id} size="body2" color="var(--colors-neutral-text-weak)">
        {content as string}
      </S.Text>
    ),
    list: (id, content) => (
      <UnorderedList key={id} items={content} />
    ),
    link: (id, content) => {
      const LinkIcon = (content as { icon: React.ComponentType<{ size?: number }> }).icon
      return (
        <Link key={id} onClick={() => handleLinkItemClick(content)} size="default">
          <Text size="body2" weight="medium" color="var(--colors-blue-text-default)">
            {content.label}
          </Text>
          <LinkIcon size={12} />
        </Link>
      )
    },
    checklist: (id, content) => (
      <ul key={id}>
        {(content as ChecklistItemProps[]).map(({ id: itemId, text }) => (
          <S.ChecklistItem key={itemId}>
            <CheckIcon color="var(--colors-cyan-icon-default)" className="checklist-icon" />
            <Text size="body2" color="var(--colors-neutral-text-weak)">
              {text}
            </Text>
          </S.ChecklistItem>
        ))}
      </ul>
    ),
    node: (id, content) => <S.NodeContent key={id}>{content}</S.NodeContent>
  }

  return (
    <Section $bg={$bg}>
      <Grid col={{ phone: 1, tablet: 2 }}>
        <Col col={{ desktop: 4 }} start={{ desktop: 2 }}>
          <Infos>{children}</Infos>
        </Col>
        <Col col={{ desktop: 5 }} start={{ tablet: 2, desktop: 7 }}>
          <S.AccordionWrapper>
            {accordion.map(({ id, title, item }: AccordionProps) => (
              <div key={id} onClick={() => handleAccordionClick(title)}>
                <AccordionNoRadix
                  key={id}
                  id={id}
                  title={
                    <Text as="h3" size="heading4" weight="medium" color="var(--colors-neutral-text-default)">
                      {title}
                    </Text>
                  }
                >
                  {item.map(({ id, type, content }: ItemProps) => renderItem[type](id, content))}
                </AccordionNoRadix>
              </div>
            ))}
          </S.AccordionWrapper>
          <AnchorButton
            href="/ajuda/"
            dataLayer={{
              category: 'Clique',
              action: 'Ajuda',
              label: 'Botão Veja mais Dúvidas'
            }}
          >
            Veja mais dúvidas
            <ArrowRightIcon size={24} />
          </AnchorButton>
        </Col>
      </Grid>
    </Section>
  )
})

Help.displayName = 'Help'
